import type { WorkflowDefinition } from "../types";

/**
 * W19 — Track a product launch in AI from day one.
 *
 * Organization-scoped because it starts before the subject exists: step one
 * creates a PRODUCT property and the run attaches to it. `perTarget` on that
 * property, so a team launching three products in the same quarter gets three
 * checklists rather than one they take turns on.
 *
 * A product has no crawlable site of its own, so the snapshot is typed in by
 * hand rather than read from a page. That is also why the counts on the new
 * product start at zero, which is what the exemptions in `catalog.test.ts`
 * record: nothing on a property this run just created can predate the run.
 */
export const productLaunchWorkflow: WorkflowDefinition = {
	id: "product-launch",
	wNumber: 19,
	title: "Track a product launch in AI from day one",
	outcome: "Have the launch measured in AI answers before the product ships.",
	audiences: ["inhouse"],
	uses: [
		"Properties",
		"Brand Snapshot",
		"Prompts",
		"Competitors",
		"Projects",
		"Annotations",
		"AI Visibility Rankings",
	],
	scope: "organization",
	bindsProperty: true,
	bindSubjectType: "PRODUCT",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "property", label: "Product" },
	// The subject this playbook creates is a PRODUCT by construction, so there
	// is nothing about the current property to gate on.
	isApplicable: () => true,
	articleSlug: "how-to-launch-a-product-with-ai-visibility-from-day-one",
	articleStatus: "published",
	caveats: [
		"Before launch, AI has nothing to say about the product. Zeros are the right answer until coverage and indexed pages exist.",
	],
	status: "live",
	steps: [
		{
			id: "add-product",
			title: "Add the product as its own subject",
			page: "Organization dashboard",
			route: "org:/",
			criterion: "You've added the product.",
			why: "A product tracked inside the parent brand disappears into it. Its own subject keeps the launch numbers separate from day one.",
			docsHref: "/docs/ai-visibility-features/product-tracking",
			tasks: [
				"Add a property with the product as the subject",
				"Choose manual entry, since there is no page to read yet",
				"Use the name buyers will actually type",
			],
			complete: { kind: "bound", subjectType: "PRODUCT" },
			// Completion is this checklist attaching to a product, not the
			// organization having products in it: the third launch of the year
			// is not done because the first two happened.
			signal: ({ run }) => ({
				kind: "checklist",
				items: [
					{
						label: "Product added",
						done: Boolean(run?.propertyId),
					},
				],
			}),
		},
		{
			id: "fill-snapshot",
			title: "Write down what the product is",
			page: "Brand Snapshot",
			route: "/brand-snapshot",
			criterion: "You've filled in at least three fields.",
			why: "There is no live page to read before launch, so you supply the ground truth. Everything measured later is measured against it.",
			docsHref: "/docs/dashboards/brand-snapshot",
			tasks: [
				"Write the features, the pricing and what makes it different",
				"Say which problems it solves, and for whom",
				"Come back and correct it once the pages are live",
			],
			complete: {
				kind: "count",
				key: "snapshotOverrideFieldCount",
				gte: 3,
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Fields you filled in",
							value: state.property.snapshotOverrideFieldCount,
							target: 3,
						}
					: null,
		},
		{
			id: "launch-prompts",
			title: "Add the questions buyers will ask",
			page: "Prompts",
			route: "/discovery-queries",
			criterion:
				"You've tagged launch prompts and kept the identity ones.",
			why: "Category questions are where a new product gets discovered. Identity questions check that AI knows what it even is.",
			docsHref: "/docs/getting-started/choosing-prompts",
			tasks: [
				"Generate category entry point and comparison prompts",
				"Keep the identity prompts that ask what the product is",
				"Tag them all with the launch",
			],
			complete: {
				kind: "all",
				of: [
					{ kind: "promptTag", tag: "launch" },
					{ kind: "count", key: "brandPromptCount", gte: 1 },
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: 'Prompts tagged "launch"',
									done: state.property.promptTags.includes(
										"launch",
									),
								},
								{
									label: `${state.property.brandPromptCount} identity prompts`,
									done: state.property.brandPromptCount > 0,
								},
							],
						}
					: null,
		},
		{
			id: "competing-products",
			title: "Name the products it will be compared with",
			page: "Competitors",
			route: "/competitors",
			criterion: "You've added at least one competing product.",
			why: "Share of voice is measured against this list. For a launch it is other products, not the parent brand's usual rivals.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Add the products a buyer would shortlist beside this one",
				"Add the other names and domains each one uses",
			],
			complete: { kind: "count", key: "competitorCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Competing products",
							value: state.property.competitorCount,
							target: 1,
						}
					: null,
		},
		{
			id: "project-and-note",
			title: "Start daily tracking and note the launch",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've set up a project and noted the launch.",
			why: "Tracking from before launch day is what makes the curve readable. The note is what explains the step change weeks later.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create a project and add the launch prompts",
				"Set the start date to before launch day",
				"Note launch day, then each wave of coverage",
			],
			complete: {
				kind: "all",
				of: [
					{ kind: "count", key: "activeProjectCount", gte: 1 },
					{ kind: "since", event: "annotationCreated" },
				],
			},
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Active project",
									done: state.property.activeProjectCount > 0,
								},
								{
									label: `${state.property.manualAnnotationCount} events noted`,
									done:
										state.property.manualAnnotationCount >
										0,
								},
							],
						}
					: null,
		},
		{
			id: "watch-pickup",
			title: "Watch AI pick the product up",
			page: "AI Visibility Rankings",
			route: "/ai-visibility-rankings",
			criterion: "You've got daily data flowing.",
			why: "The first weeks are mostly zeros, and that is the baseline you needed. Movement starts once pages are crawled and coverage lands.",
			docsHref: "/docs/dashboards/ai-visibility-rankings",
			tasks: [
				"Watch rank and share of voice on the launch questions",
				"Check the launch pages have been crawled",
				"Read the early zeros as the starting line",
			],
			complete: { kind: "flag", key: "hasNightlyMetrics" },
			enrichment: "rankTrend",
			// Rank history is not in the loader; the step panel hydrates it
			// from the SEO-opportunities hook rather than inventing a series.
			signal: () => null,
		},
	],
};
